import { useEffect, useMemo, useRef, useState } from 'react';
import type { RefObject } from 'react';
import {
  adaptManualFrameStyleToViewport,
  computeCriticalVideoStyle,
  computeManualVideoStyle,
  isCompatibilityVideoFramingBrowser,
  selectCriticalBoundingBox,
  selectViewportProfile,
} from '@/lib/videoFraming';
import type { CameraVideoViewportSettings } from './teleopConfig';

type ViewportSize = {
  widthPx: number;
  heightPx: number;
};

type UseTeleopViewportLayoutParams = {
  activeCamera: string;
  secondaryTrack: string;
  mobileMaxWidthPx: number;
  tabletMaxWidthPx: number;
  sourceWidthPx: number;
  sourceHeightPx: number;
  overheadVideoViewport: CameraVideoViewportSettings;
  perspectiveVideoViewport: CameraVideoViewportSettings;
};

type UseTeleopViewportLayoutResult = {
  containerRef: RefObject<HTMLDivElement | null>;
  viewportSize: ViewportSize;
  activeViewportProfile: ReturnType<typeof selectViewportProfile>;
  activeVideoViewportBoundingBox: ReturnType<typeof selectCriticalBoundingBox>;
  videoStyle: ReturnType<typeof computeManualVideoStyle>;
  isMobileLandscapeMode: boolean;
  canShowDesktopSelfieHideControl: boolean;
  showDesktopRotationKeyboardHint: boolean;
  cameraControlsPositionClass: string;
  earlyAccessPilotPositionClass: string;
};

export const useTeleopViewportLayout = ({
  activeCamera,
  secondaryTrack,
  mobileMaxWidthPx,
  tabletMaxWidthPx,
  sourceWidthPx,
  sourceHeightPx,
  overheadVideoViewport,
  perspectiveVideoViewport,
}: UseTeleopViewportLayoutParams): UseTeleopViewportLayoutResult => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [viewportSize, setViewportSize] = useState<ViewportSize>({
    widthPx: window.innerWidth,
    heightPx: window.innerHeight,
  });
  const [usesCompatibilityVideoFraming] = useState<boolean>(() =>
    isCompatibilityVideoFramingBrowser()
  );

  const activeVideoViewportSettings = useMemo(() => {
    const isSecondaryTrack = activeCamera === secondaryTrack;
    const nextViewportSettings = isSecondaryTrack
      ? perspectiveVideoViewport
      : overheadVideoViewport;

    return nextViewportSettings;
  }, [
    activeCamera,
    overheadVideoViewport,
    perspectiveVideoViewport,
    secondaryTrack,
  ]);

  const activeViewportProfile = useMemo(() => {
    const nextViewportProfile = selectViewportProfile({
      viewportWidthPx: viewportSize.widthPx,
      viewportHeightPx: viewportSize.heightPx,
      mobileMaxWidthPx,
      tabletMaxWidthPx,
    });

    return nextViewportProfile;
  }, [
    mobileMaxWidthPx,
    tabletMaxWidthPx,
    viewportSize.heightPx,
    viewportSize.widthPx,
  ]);

  const activeVideoViewportBoundingBox = useMemo(() => {
    const nextBoundingBox = selectCriticalBoundingBox({
      viewportWidthPx: viewportSize.widthPx,
      viewportHeightPx: viewportSize.heightPx,
      mobileMaxWidthPx,
      tabletMaxWidthPx,
      criticalBoundingBoxByScreen:
        activeVideoViewportSettings.criticalBoundingBoxByScreen,
    });

    return nextBoundingBox;
  }, [
    activeVideoViewportSettings,
    mobileMaxWidthPx,
    tabletMaxWidthPx,
    viewportSize.heightPx,
    viewportSize.widthPx,
  ]);

  const activeManualFrameStyle =
    activeVideoViewportSettings.manualFrameStyleByScreen[activeViewportProfile];

  const videoStyle = useMemo(() => {
    if (activeManualFrameStyle) {
      const adaptedFrameStyle = adaptManualFrameStyleToViewport({
        frameStyle: activeManualFrameStyle,
        referenceViewport:
          activeVideoViewportSettings.manualFrameReferenceViewport,
        targetViewportWidthPx: viewportSize.widthPx,
        targetViewportHeightPx: viewportSize.heightPx,
      });
      const nextVideoStyle = computeManualVideoStyle(adaptedFrameStyle);

      return nextVideoStyle;
    }

    if (usesCompatibilityVideoFraming) {
      const compatibilityStyle = computeManualVideoStyle({
        leftPct: 0,
        topPct: 0,
        widthPct: 100,
        heightPct: 100,
        objectPositionXPct:
          activeVideoViewportBoundingBox.leftPct +
          activeVideoViewportBoundingBox.widthPct / 2,
        objectPositionYPct:
          activeVideoViewportBoundingBox.topPct +
          activeVideoViewportBoundingBox.heightPct / 2,
      });

      return compatibilityStyle;
    }

    const nextVideoStyle = computeCriticalVideoStyle({
      viewportWidthPx: viewportSize.widthPx,
      viewportHeightPx: viewportSize.heightPx,
      sourceWidthPx,
      sourceHeightPx,
      criticalBox: activeVideoViewportBoundingBox,
    });

    return nextVideoStyle;
  }, [
    activeManualFrameStyle,
    activeVideoViewportBoundingBox,
    activeVideoViewportSettings,
    usesCompatibilityVideoFraming,
    sourceHeightPx,
    sourceWidthPx,
    viewportSize.heightPx,
    viewportSize.widthPx,
  ]);

  useEffect(() => {
    const containerElement = containerRef.current;

    if (!containerElement) {
      return;
    }

    const updateViewportSize = () => {
      const rect = containerElement.getBoundingClientRect();
      const hasVisibleSize = rect.width > 0 && rect.height > 0;

      if (!hasVisibleSize) {
        return;
      }

      const nextViewportSize = {
        widthPx: rect.width,
        heightPx: rect.height,
      };

      setViewportSize(nextViewportSize);
    };

    const resizeObserver = new ResizeObserver(updateViewportSize);

    resizeObserver.observe(containerElement);
    updateViewportSize();

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const isLandscape = viewportSize.widthPx > viewportSize.heightPx;
  const shortViewportSidePx = Math.min(
    viewportSize.widthPx,
    viewportSize.heightPx,
  );
  const isMobileWidthLayout = viewportSize.widthPx <= mobileMaxWidthPx;
  const isMobileLandscapeMode =
    isLandscape &&
    shortViewportSidePx <= mobileMaxWidthPx;
  const canShowDesktopSelfieHideControl =
    typeof window !== 'undefined' &&
    window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const showDesktopRotationKeyboardHint = canShowDesktopSelfieHideControl;
  const pinCameraControlsToTop =
    isMobileLandscapeMode ||
    canShowDesktopSelfieHideControl;
  const cameraControlsPositionClass = pinCameraControlsToTop
    ? 'top-[calc(env(safe-area-inset-top,0px)+12px)] max-[600px]:top-[calc(env(safe-area-inset-top,0px)+10px)]'
    : 'top-[calc(env(safe-area-inset-top,0px)+78px)] max-[600px]:top-[calc(env(safe-area-inset-top,0px)+70px)]';
  const earlyAccessPilotPositionClass = isMobileWidthLayout
    ? 'left-2 top-[calc(env(safe-area-inset-top,0px)+40px)]'
    : pinCameraControlsToTop
      ? 'left-4 top-[calc(env(safe-area-inset-top,0px)+52px)] max-[900px]:left-3'
      : 'left-1/2 top-[calc(env(safe-area-inset-top,0px)+16px)] -translate-x-1/2';

  return {
    containerRef,
    viewportSize,
    activeViewportProfile,
    activeVideoViewportBoundingBox,
    videoStyle,
    isMobileLandscapeMode,
    canShowDesktopSelfieHideControl,
    showDesktopRotationKeyboardHint,
    cameraControlsPositionClass,
    earlyAccessPilotPositionClass,
  };
};
